// Client-side mirror of firestore.rules visibility. The rules are the real
// enforcement; this just keeps the UI from showing rows the user can't act on
// and from firing queries that would be rejected.

import type { Action, Goal, Initiative, NamedRecord, Task, UserProfile } from '../types';

export function isAdmin(p: UserProfile | null): boolean {
  return p?.role === 'admin';
}

/** Holds a Position with `lead` set — full edit + task-create authority. */
export function isChair(p: UserProfile | null): boolean {
  return p?.role === 'chair';
}

/** Holds a district-wide Position — may view everything in their district
 *  (and manage the roster), whether or not they can edit. */
export function isDistrictWide(p: UserProfile | null): boolean {
  return !!p?.districtView;
}

/** Holds a Key 3 Position (District Chair/Commissioner/Executive) — the top
 *  non-admin tier. Can manage any OTHER non-admin login in their district,
 *  on top of whatever chair/member access they otherwise have. */
export function isKeyThree(p: UserProfile | null): boolean {
  return !!p?.keyThree;
}

/** Legacy — superseded by councilAccess (Council Access Phase A). No
 *  longer an active authorization source anywhere in this file; kept only
 *  so historical/rollback code paths can still read the raw flag. Use
 *  hasCouncilFullRead()/hasCouncilFullRW() below instead. */
export function isCouncilWide(p: UserProfile | null): boolean {
  return !!p?.councilWide;
}

/** Council-level operational access, tri-state and ordered ('fullRW'
 *  implies 'fullRead' implies 'dashboardOnly'). Explicit per (tenant,
 *  membership) — never derived from a Position or org-chart placement.
 *  Admin-settable only, via Settings > Users & Access. Independent of
 *  contactAccess below — Full Read/RW never implies it. */
export function hasCouncilDashboardAccess(p: UserProfile | null): boolean {
  const a = p?.councilAccess;
  return a === 'dashboardOnly' || a === 'fullRead' || a === 'fullRW';
}

/** Council-wide READ access to normal operational Compass data — the
 *  direct replacement for the old isCouncilWide(). */
export function hasCouncilFullRead(p: UserProfile | null): boolean {
  const a = p?.councilAccess;
  return a === 'fullRead' || a === 'fullRW';
}

/** Council-wide operational READ/WRITE access. Not administrator access —
 *  see canManageQualifications and friends for what stays admin-only. */
export function hasCouncilFullRW(p: UserProfile | null): boolean {
  return p?.councilAccess === 'fullRW';
}

/** Protected contact-field (email/phone/notes) visibility, independent of
 *  councilAccess — Full Read/RW never implies this. Additive to whatever
 *  Position/relationship-based Full visibility a member already has. */
export function hasContactAccessRead(p: UserProfile | null): boolean {
  return p?.contactAccess === 'read';
}

/** Holds specifically the "District Commissioner" Position — narrower than
 *  isKeyThree(), which also covers District Chair/Executive. Matched by
 *  name (no dedicated flag distinguishes it — see firestore.rules'
 *  matching isDistrictCommissioner()). Only this position may create/edit/
 *  remove Unit Commissioner assignments. */
export function isDistrictCommissioner(p: UserProfile | null): boolean {
  return !!p?.positions?.includes('District Commissioner');
}

function committeeMatch(p: UserProfile, committee: string): boolean {
  return (p.committees ?? []).includes(committee);
}

/** Chair edit-scope for a goal/task-shaped resource: same district AND the
 *  item's committee is one this chair leads. */
function chairCanSee(
  p: UserProfile,
  item: { districtId?: string; districtIds?: string[]; committee: string },
): boolean {
  if (!isChair(p) || !committeeMatch(p, item.committee)) return false;
  if (item.districtId) return item.districtId === p.districtId;
  if (item.districtIds) return !!p.districtId && item.districtIds.includes(p.districtId);
  return false;
}

function inMyDistrict(
  p: UserProfile,
  item: { districtId?: string; districtIds?: string[] },
): boolean {
  if (!p.districtId) return false;
  if (item.districtId) return item.districtId === p.districtId;
  return (item.districtIds ?? []).includes(p.districtId);
}

// Goals are council-level strategy — every signed-in user sees every goal,
// regardless of district. (Tasks and Initiatives stay district-scoped —
// see canSeeTask / canSeeInitiative.)
export function canSeeGoal(p: UserProfile | null, _goal: Goal): boolean {
  return !!p;
}

export function memberAssignedToTask(p: UserProfile, task: Task): boolean {
  if (task.assignedPosition && (p.positions ?? []).includes(task.assignedPosition)) {
    return true;
  }
  if (p.personId && task.assignedPersonIds && task.assignedPersonIds.includes(p.personId)) return true;
  if (
    task.assignedContactIds &&
    p.contactId &&
    task.assignedContactIds.includes(p.contactId)
  ) {
    return true;
  }
  return false;
}

// Anyone in the district reads every task in it, regardless of committee or
// assignment — editing stays gated separately (canEditTaskFully /
// canUpdateTaskStatus), by position hierarchy or direct assignment.
export function canSeeTask(p: UserProfile | null, task: Task): boolean {
  if (!p) return false;
  if (isAdmin(p)) return true;
  return inMyDistrict(p, task);
}

/** Full edit (all fields, delete, reassign). District-wide view alone does
 *  not grant this — only admin or the sub-committee lead. */
export function canEditTaskFully(p: UserProfile | null, task: Task): boolean {
  if (!p) return false;
  if (isAdmin(p)) return true;
  return chairCanSee(p, task);
}

/** Members may only touch status/notes on tasks assigned to them. */
export function canUpdateTaskStatus(p: UserProfile | null, task: Task): boolean {
  if (canEditTaskFully(p, task)) return true;
  return !!p && memberAssignedToTask(p, task);
}

/** Can create tasks under this goal (sub-committee lead or admin). */
export function canCreateTaskForGoal(p: UserProfile | null, goal: Goal): boolean {
  if (!p) return false;
  if (isAdmin(p)) return true;
  return isChair(p) && !!p.districtId && (goal.districtIds ?? []).includes(p.districtId)
    && committeeMatch(p, goal.committee);
}

/** Initiative visible to this user — anyone in the district, like goals. */
export function canSeeInitiative(p: UserProfile | null, initiative: Initiative): boolean {
  if (!p) return false;
  if (isAdmin(p)) return true;
  return inMyDistrict(p, initiative);
}

/** Create / edit / archive an initiative: admin, or the committee lead for its
 *  district + committee (mirrors firestore.rules `chairCanSee`). */
export function canManageInitiative(
  p: UserProfile | null,
  initiative: { districtId: string; committee: string },
): boolean {
  if (!p) return false;
  if (isAdmin(p)) return true;
  return chairCanSee(p, initiative);
}

/** Can this user adopt a template / start a new initiative at all (in some
 *  district + committee)? Drives whether the "Adopt" button shows. */
export function canStartInitiative(p: UserProfile | null): boolean {
  if (!p) return false;
  return isAdmin(p) || (isChair(p) && !!p.districtId && (p.committees ?? []).length > 0);
}

/** Committees this user may file an initiative under. */
export function initiativeCommittees(
  p: UserProfile | null,
  allCommittees: NamedRecord[],
): NamedRecord[] {
  if (!p) return [];
  if (isAdmin(p)) return allCommittees;
  return allCommittees.filter((c) => (p.committees ?? []).includes(c.id));
}

/** Can add / edit / remove District Roster (contacts) and Org Chart
 *  assignments, and grant a login from that same screen — any Chair
 *  Position (Tier 2) or Key 3 (Tier 1), each for their own district. Tier 2
 *  can only ever grant a plain member-level login; Tier 1 can grant or
 *  remove any non-admin level. Admin only grants admin access, still via
 *  Settings. See firestore.rules' matching `managesRoster()`. */
export function canManageRoster(p: UserProfile | null, districtId: string): boolean {
  if (!p) return false;
  if (isAdmin(p)) return true;
  return (isChair(p) || isKeyThree(p)) && p.districtId === districtId;
}

/** Districts this user should be offered in filters/forms. */
export function visibleDistricts(
  p: UserProfile | null,
  allDistricts: NamedRecord[],
): NamedRecord[] {
  if (!p) return [];
  if (isAdmin(p) || hasCouncilFullRead(p)) return allDistricts;
  return allDistricts.filter((d) => d.id === p.districtId);
}

// ---- Actions -----------------------------------------------------------
// Mirrors firestore.rules' isActionOwner() / isActionParticipant() /
// the `actions` match block exactly — see that file for the actual
// enforcement.

export function isActionOwner(p: UserProfile | null, a: { ownerContactId: string; ownerPersonId?: string }): boolean {
  if (p?.personId && a.ownerPersonId && p.personId === a.ownerPersonId) return true;
  return !!p?.contactId && p.contactId === a.ownerContactId;
}

export function isActionParticipant(
  p: UserProfile | null,
  a: { participantContactIds: string[]; participantPersonIds?: string[] },
): boolean {
  if (p?.personId && a.participantPersonIds?.includes(p.personId)) return true;
  return !!p?.contactId && a.participantContactIds.includes(p.contactId);
}

/** Anyone in the Action's district sees it (like Tasks), regardless of
 *  ownership — plus admin/council-wide, plus the owner/a participant even
 *  if (unusually) their own district differs from the Action's. */
export function canSeeAction(p: UserProfile | null, a: Action): boolean {
  if (!p) return false;
  if (isAdmin(p) || hasCouncilFullRead(p)) return true;
  if (p.districtId === a.districtId) return true;
  return isActionOwner(p, a) || isActionParticipant(p, a);
}

/** Full edit — content, workflow, owner, and participants (but not delete):
 *  admin, Key 3 for that district (matches their existing broad
 *  district-scoped authority elsewhere), or the Action's own owner. A
 *  chair does NOT get this automatically — Actions aren't committee-scoped
 *  the way Tasks are, so there's no chair-committee match to key off. */
export function canEditActionFully(p: UserProfile | null, a: Action): boolean {
  if (!p) return false;
  if (isAdmin(p)) return true;
  if (isKeyThree(p) && p.districtId === a.districtId) return true;
  return isActionOwner(p, a);
}

/** Participants (and anyone who can fully edit) may add a note and see
 *  history — the Phase 1 participant contract: view, comment, watch. */
export function canCommentOnAction(p: UserProfile | null, a: Action): boolean {
  if (canEditActionFully(p, a)) return true;
  return isActionParticipant(p, a);
}

/** Who may create an Action at all — deliberately permissive (any
 *  signed-in user with a home district, not gated to chair/admin) per the
 *  "Action Changes Things" low-friction intent; accountability still flows
 *  through the owner, not the creator. */
export function canCreateAction(p: UserProfile | null): boolean {
  if (!p) return false;
  return isAdmin(p) || !!p.districtId;
}

/** Client-side Action deletion is intentionally unavailable. Everyone
 *  resolves an Action by completing or cancelling it; exceptional cleanup
 *  is a server-only Admin SDK operation. */
export function canDeleteAction(_p: UserProfile | null, _a: Action): boolean {
  return false;
}

// ---- Qualifications ------------------------------------------------------

/** Admin-only for this increment (mirrors Goals: council-wide read, admin
 *  write — see firestore.rules' matching `qualificationAreas`/
 *  `qualificationAssignments` blocks). Compass already has real
 *  infrastructure for exactly this kind of future delegation — the
 *  capability policy matrix (components/PermissionsMatrix.tsx,
 *  hooks/usePermissionsAdministration.ts, functions/src/permissionsAdmin.ts)
 *  lets a capability be granted to a Position at a given scope, but nothing
 *  outside the matrix's own admin UI reads from it yet (every capability's
 *  `enforcement.status` there is still 'notYetEnforcedHere' or
 *  'separateAccessRules' except a couple of already-migrated domains) — so
 *  registering a `qualifications` domain/capability there and having THIS
 *  function consult it is the real seam for "Council Shooting Sports Chair
 *  manages just Shooting Sports," deliberately not built in this increment.
 *  This single choke point is what makes that a later, additive change
 *  instead of a refactor. */
export function canManageQualifications(p: UserProfile | null): boolean {
  return isAdmin(p);
}
